// Right-column grading section: score ring, criteria checklist, summary,
// coaching feedback, positives/negatives/violations, next steps.
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { CheckCircle, XCircle, AlertTriangle, Info, Trophy, MinusCircle, Sparkles } from 'lucide-react';
import { getScoreColor } from './callDetailUtils';

const ScoreRing = ({ grading }) => {
  const isWon = !!grading.is_won;
  const adjusted = grading.overall_score_adjusted;
  // On won calls we show the ADJUSTED score in the ring (with unmet
  // criteria dropped from the denominator). The raw score is preserved
  // in a small note underneath so nothing gets hidden.
  const displayed = isWon && typeof adjusted === 'number' ? adjusted : grading.overall_score;
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - displayed / 100);
  return (
    <Card className={`border-slate-200 ${isWon ? 'ring-2 ring-emerald-200' : ''}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {isWon && <Trophy className="w-4 h-4 text-emerald-600" />}
          {isWon ? 'Adjusted Score (Won)' : 'Overall Score'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-center">
          <div className="relative w-32 h-32">
            <svg className="w-full h-full transform -rotate-90">
              <circle cx="64" cy="64" r={radius} stroke="#E2E8F0" strokeWidth="12" fill="none" />
              <circle
                cx="64" cy="64" r={radius}
                stroke="currentColor"
                strokeWidth="12"
                fill="none"
                strokeDasharray={`${circumference}`}
                strokeDashoffset={`${offset}`}
                className={`score-ring ${getScoreColor(displayed)}`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-3xl font-bold ${getScoreColor(displayed)}`}>
                {displayed}
              </span>
            </div>
          </div>
        </div>
        {isWon && typeof adjusted === 'number' && adjusted !== grading.overall_score && (
          <p className="text-center text-[11px] text-slate-500 mt-2">
            Raw graded score: <span className="font-semibold">{grading.overall_score}</span>
            <span className="text-slate-400"> · uncovered criteria dropped because the deal closed</span>
          </p>
        )}
        <p className="text-center text-sm text-slate-500 mt-4">
          Graded by {grading.model_used === 'demo-seed' ? 'Behavioral Analysis Engine' : (grading.model_used || 'AI')}
        </p>
        {grading.call_intent && (
          <div
            className="mt-3 flex items-center justify-center gap-2"
            data-testid="call-intent-badge"
          >
            <span className="text-[10px] uppercase tracking-wide text-slate-400">
              Intent
            </span>
            <span
              className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                grading.call_intent === 'initial_sales'
                  ? 'bg-sky-50 text-sky-700 border-sky-200'
                  : grading.call_intent === 'follow_up'
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : 'bg-slate-100 text-slate-700 border-slate-200'
              }`}
            >
              {grading.call_intent.replace('_', ' ')}
            </span>
            {grading.prior_calls_used > 0 && (
              <span
                className="text-[10px] text-slate-400"
                title="Number of prior calls with this customer fed into the AI as context"
              >
                · {grading.prior_calls_used} prior
              </span>
            )}
          </div>
        )}
        {grading.call_subtype && (
          <div
            className="mt-4 pt-4 border-t border-slate-100 text-center"
            data-testid="call-subtype-badge"
          >
            <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
              Detected Sub-Intent
            </p>
            <p className="text-sm font-medium text-slate-700">
              {grading.call_subtype_label || grading.call_subtype}
            </p>
            {grading.effective_threshold != null && (
              <p className="text-[11px] text-slate-400 mt-1">
                Escalates below {grading.effective_threshold}
              </p>
            )}
            {grading.subtype_evidence && (
              <p className="text-[11px] text-slate-500 mt-2 italic px-2">
                &ldquo;{grading.subtype_evidence}&rdquo;
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const CriteriaList = ({ results, isWon }) => {
  // Split into applicable + N/A-for-type so the N/A ones can be shown in a
  // muted collapsed section below the main checklist. This keeps managers
  // focused on what actually mattered for this call intent.
  const applicable = results.filter((r) => !r.not_applicable_for_type);
  const notApplicable = results.filter((r) => r.not_applicable_for_type);
  return (
    <Card className="border-slate-200">
      <CardHeader>
        <CardTitle>Criteria Checklist</CardTitle>
        <CardDescription>
          {applicable.length} items evaluated
          {notApplicable.length > 0 && (
            <span className="ml-1 text-slate-500">
              · {notApplicable.length} not applicable to this call type
            </span>
          )}
          {isWon && (
            <span className="ml-1 text-emerald-700 font-medium">
              · uncovered items marked &quot;Not needed&quot; because the deal closed
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {applicable.map((criteria, index) => {
            const notNeeded = isWon && criteria.met === false;
            const rowCls = notNeeded ? 'bg-slate-50/60 opacity-75' : 'bg-slate-50';
            return (
              <div
                key={criteria.description || index}
                className={`flex items-start space-x-3 p-3 rounded-lg criteria-item ${rowCls}`}
                data-testid={`criteria-${index}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {criteria.met ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  ) : notNeeded ? (
                    <MinusCircle className="w-5 h-5 text-slate-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-rose-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${notNeeded ? 'text-slate-500' : 'text-slate-900'}`}>
                    {criteria.description}
                    {notNeeded && (
                      <span className="ml-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                        Not needed
                      </span>
                    )}
                  </p>
                  {criteria.evidence && !notNeeded && (
                    <p className="text-xs text-slate-600 mt-1 italic">
                      &ldquo;{criteria.evidence}&rdquo;
                    </p>
                  )}
                  {notNeeded && (
                    <p className="text-[11px] text-slate-500 mt-1">
                      Deal closed without this step — no coaching penalty applied.
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {notApplicable.length > 0 && (
          <details
            className="mt-4 pt-3 border-t border-slate-100"
            data-testid="not-applicable-section"
          >
            <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700 select-none">
              Not applicable to this call type ({notApplicable.length}) — click to expand
            </summary>
            <div className="space-y-2 mt-2">
              {notApplicable.map((criteria, index) => (
                <div
                  key={`na-${criteria.description || index}`}
                  className="flex items-start space-x-3 p-2.5 rounded-lg bg-slate-50/50 border border-dashed border-slate-200"
                  data-testid={`criteria-na-${index}`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <MinusCircle className="w-4 h-4 text-slate-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-500">
                      {criteria.description}
                      <span className="ml-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                        N/A for this call
                      </span>
                    </p>
                    {Array.isArray(criteria.applies_to) && criteria.applies_to.length > 0 && (
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Only scored on: {criteria.applies_to.map((k) => k.replace('_', ' ')).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
};

// Jump button between the score ring and the summary — scrolls to the
// full playbook card at the bottom and kicks off generation (same
// `vf-open-playbook` event the header button uses).
const PlaybookJumpCard = () => (
  <Card className="border-teal-200 bg-gradient-to-br from-teal-50/70 to-white">
    <CardContent className="py-3 flex items-center gap-3">
      <button
        onClick={() => window.dispatchEvent(new Event('vf-open-playbook'))}
        className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg px-4 py-2 flex-shrink-0"
        data-testid="sidebar-playbook-btn"
      >
        <Sparkles className="w-4 h-4" /> Generate playbook
      </button>
      <p className="text-[11px] text-slate-500 leading-snug">
        DISC-tuned callback script with objection handling — opens below.
      </p>
    </CardContent>
  </Card>
);

const SummaryCard = ({ summary }) => (
  <Card className="border-blue-200 bg-blue-50">
    <CardHeader className="pb-2">
      <CardTitle className="text-blue-900 text-base flex items-center gap-2">
        <Info className="w-4 h-4" />
        Call Summary
      </CardTitle>
    </CardHeader>
    <CardContent>
      <p className="text-sm text-blue-800 leading-relaxed">{summary}</p>
    </CardContent>
  </Card>
);

const FeedbackCard = ({ text }) => (
  <Card className="border-slate-200">
    <CardHeader><CardTitle>Coaching Feedback</CardTitle></CardHeader>
    <CardContent>
      <p className="text-sm text-slate-700 leading-relaxed">{text}</p>
    </CardContent>
  </Card>
);

// Generic colored list card used for positives / negatives / violations.
const BulletListCard = ({ title, items, tone }) => {
  const styles = {
    positive: { card: 'border-emerald-200 bg-emerald-50', text: 'text-emerald-900', li: 'text-emerald-800', Icon: CheckCircle },
    negative: { card: 'border-rose-200 bg-rose-50', text: 'text-rose-900', li: 'text-rose-800', Icon: XCircle },
    violation: { card: 'border-red-200 bg-red-50', text: 'text-red-900', li: 'text-red-800', Icon: AlertTriangle },
  };
  const { card, text, li, Icon } = styles[tone];
  return (
    <Card className={card}>
      <CardHeader><CardTitle className={text}>{title}</CardTitle></CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {items.map(item => (
            <li key={item} className={`flex items-start space-x-2 text-sm ${li}`}>
              <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

const NextStepsCard = ({ steps }) => (
  <Card className="border-slate-200">
    <CardHeader><CardTitle>Recommended Next Steps</CardTitle></CardHeader>
    <CardContent>
      <ol className="list-decimal list-inside space-y-2">
        {steps.map((step, index) => (
          <li key={`step-${index}-${step.slice(0, 20)}`} className="text-sm text-slate-700">
            {step}
          </li>
        ))}
      </ol>
    </CardContent>
  </Card>
);

const PendingCard = () => (
  <Card className="border-slate-200">
    <CardContent className="pt-6 text-center">
      <p className="text-slate-500">Grading in progress...</p>
      <p className="text-sm text-slate-400 mt-2">This may take a moment</p>
    </CardContent>
  </Card>
);

export const GradingSidebar = ({ grading, playbookAvailable }) => {
  if (!grading) return <PendingCard />;
  const isWon = !!grading.is_won;

  // Card ordering per manager feedback: score → summary → good/bad/violations
  // → next steps → criteria checklist. The checklist is the most detailed
  // (and longest) block, so surfacing the narrative first lets a coach
  // scan the story before drilling into the item-by-item audit.
  //
  // On WON calls the "Areas for Improvement" card is suppressed — the
  // deal closed, so lecturing the agent on missed steps is
  // counter-productive. The criteria checklist below still shows every
  // item (reframed as "Not needed") for full transparency.
  return (
    <>
      <ScoreRing grading={grading} />
      {playbookAvailable && <PlaybookJumpCard />}
      {grading.call_summary && <SummaryCard summary={grading.call_summary} />}
      <FeedbackCard text={grading.coaching_feedback} />
      {grading.positives.length > 0 && (
        <BulletListCard title="Positive Behaviors" items={grading.positives} tone="positive" />
      )}
      {!isWon && grading.negatives.length > 0 && (
        <BulletListCard title="Areas for Improvement" items={grading.negatives} tone="negative" />
      )}
      {grading.governance_violations.length > 0 && (
        <BulletListCard title="Governance Violations" items={grading.governance_violations} tone="violation" />
      )}
      <NextStepsCard steps={grading.recommended_next_steps} />
      <CriteriaList results={grading.criteria_results} isWon={isWon} />
    </>
  );
};
